import { Icon, Button } from 'antd'
import React from 'react'
import { FormattedHTMLMessage, useIntl } from 'react-intl'

import StickerWrapper from '../sticker/StickerWrapper'
import { MovingStatus, PackageInfo, PackType, Sticker } from '../../types'
import UploadFile from '../UploadFile'
import { useUserWithRedirect } from '../../hooks/user/useUserWithRedirect'
import {
  useDeleteSticker,
  useUpdateSticker,
} from '../../hooks/reactQuery/useSticker'
import { useUploadSticker } from '../../hooks/reactQuery/useUploadSticker'
import { notiFormForError } from '../notificationForm'
import checkImageSize from '../../utils/checkImageSize'
import { useIsMutating } from 'react-query'
import isNotPng from '../../utils/isNotPng'

const getAcceptType = (movingStatus: MovingStatus | null) => {
  switch (movingStatus) {
    case 'ANIMATED':
      return 'image/gif'
    case 'STILL':
      return 'image/png'
    default:
      return 'image/png,image/gif'
  }
}

export default function UploadSticker({
  wantedStickerNum,
  packageAndSticker,
}: {
  wantedStickerNum: number
  packageAndSticker: PackageInfo
}) {
  const user = useUserWithRedirect()
  const intl = useIntl()
  const isMutatingPackage = useIsMutating(packageAndSticker.objectId) > 0

  const stickerUploadMutate = useUploadSticker(packageAndSticker.objectId)
  const stickerUpdateMutate = useUpdateSticker(packageAndSticker.objectId)
  const stickerDeleteMutate = useDeleteSticker(packageAndSticker.objectId)

  if (!user) return <></>
  const checkImage = async (file: File) => {
    const formatMessage = intl.formatMessage
    let possible = true
    if (file.type !== 'image/png' && file.type !== 'image/gif') {
      notiFormForError(
        intl.locale,
        formatMessage({ id: 'StickerUpload.noti.stickerFormat' }),
      )
      possible = false
    }
    if (file.size > 500 * 1000) {
      notiFormForError(
        intl.locale,
        formatMessage({ id: 'StickerUpload.noti.stickerSize' }),
      )
      possible = false
    }
    if(file.type === 'image/png'){
    const blob = file.slice(0, -1, 'image/png');
      const buffer = await blob.arrayBuffer();
      if (isNotPng(buffer)) {
        // 3. apng가 아닌 png만 가능
        notiFormForError(
          intl.locale,
          formatMessage({ id: 'StickerUpload.noti.stickerFormat' }),
        )
        possible = false
      }
    }
    const possibleImageSize = await checkImageSize(file, 618, 618)
    if (!possibleImageSize) {
      notiFormForError(
        intl.locale,

        formatMessage({ id: 'StickerUpload.noti.stickerPixel' }),
      )
      possible = false
    }
    return possible
  }
  const handleUpload: React.ChangeEventHandler<HTMLInputElement> = async (
    e,
  ) => {
    if (isMutatingPackage) return
    if (e.target.files) {
      const fileLength = e.target.files.length
      if (
        fileLength + packageAndSticker.emoticons.length > 23 &&
        packageAndSticker.packType === 'BASIC'
      ) {
        notiFormForError(
          intl.locale,
          intl.formatMessage({ id: 'PkgConfirm.noti.BASIC.toManySticker' }),
        )
        return
      }
      for (const file of e.target.files) {
        if (!(await checkImage(file))) return
      }

      stickerUploadMutate.mutate({
        userId: user.objectId,
        packageId: packageAndSticker.objectId,
        sessionToken: user.sessionToken,
        fileList: e.target.files,
      })
    }
  }

  if (!packageAndSticker) return <></>
  const stickerList = packageAndSticker.emoticons
    ? packageAndSticker.emoticons
    : []

  const accept = getAcceptType(packageAndSticker.movingStatus)
  const decideStickerBorder = (sticker: Sticker) => {
    if (sticker.stickerInspectionData) {
      if (
        sticker.stickerInspectionData.needImgEditing ||
        sticker.stickerInspectionData.needTagEditing ||
        sticker.stickerInspectionData.needStickerDeleting
      ) {
        return 'yellow'
      } else if (sticker.stickerInspectionData.isPassed) {
        return 'purple'
      }
    }
    return undefined
    // if(sticker.)
  }
  return (
    <div className="sticker-upload-wrapper">
      {stickerList.length === 0 ? (
        <UploadFile
          id={'upload-first'}
          accept={accept}
          draggable={true}
          multiple
          onChange={handleUpload}
        >
          <div className="upload-sticker-drag-content">
            <div>
              <Icon
                type="plus-circle"
                theme="filled"
                style={{ color: 'rgba(0, 0, 0, 0.38)', marginRight: '8px' }}
              />
              <span>파일을 여기에 끌어다 놓거나 </span>
            </div>
            <div>
              <Button
                style={{
                  background: '#A202FF',
                  color: 'white',
                  fontWeight: 700,
                  marginTop: '8px',
                }}
              >
                여러 파일을 선택
              </Button>
            </div>
          </div>
        </UploadFile>
      ) : (
        <div className="sticker-list">
          {/* 이미 올린 스티커 */}
          {stickerList.map((sticker, idx) => (
            <StickerWrapper
              key={sticker.objectId}
              sticker={sticker}
              imgBorder={decideStickerBorder(sticker)}
            >
              {sticker.stickerInspectionData?.isPassed ? (
                <Icon
                  onClick={() => {
                    if (isMutatingPackage) return
                    stickerDeleteMutate.mutate({
                      stickerOid: sticker.objectId,
                      sessionToken: user.sessionToken,
                    })
                  }}
                  style={{ marginLeft: '12px' }}
                  type="delete"
                  className="hover-icon"
                />
              ) : (
                <UploadFile
                  id={`edit-${idx}`}
                  accept={accept}
                  onChange={(e) => {
                    if (isMutatingPackage) return
                    console.log(e.target.files)
                    if (!e.target.files) return
                    const file = e.target.files[0]
                    stickerUpdateMutate.mutate({
                      stickerId: sticker.objectId,
                      packageId: packageAndSticker.objectId,
                      userId: user.objectId,
                      sessionToken: user.sessionToken,
                      tobeFile: file,
                    })
                  }}
                  editIcon={true}
                >
                  <Icon
                    onClick={() => {
                      if (isMutatingPackage) return
                      stickerDeleteMutate.mutate({
                        stickerOid: sticker.objectId,
                        sessionToken: user.sessionToken,
                      })
                    }}
                    style={{ marginLeft: '12px' }}
                    type="delete"
                    className="hover-icon"
                  />
                </UploadFile>
              )}
            </StickerWrapper>
          ))}

          {Array.from({ length: wantedStickerNum }, (_, idx) => {
            if (idx < stickerList.length) {
              return null
            }
            return (
              <span
                className="sticker-upload-drag sticker-wrapper"
                key={`upload-${idx}`}
              >
                <UploadFile
                  multiple
                  accept={accept}
                  onChange={handleUpload}
                  key={`upload-${idx}`}
                  id={`upload-${idx}`}
                >
                  <div className="sticker-upload-card">
                    <Icon
                      type="plus-circle"
                      theme="filled"
                      style={{ marginRight: '8px', fontSize: '16px' }}
                    />
                    <FormattedHTMLMessage
                      id={'StickerUpload.select.explain'}
                      defaultMessage={'Sticker Upload'}
                    />
                  </div>
                </UploadFile>
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}
